export const ONBOARDING_USER_TYPE = {
  BROKER: 'broker',
  ORG_USER: 'orgUser',
  NORMAL_USER: 'normaluser',
};

/** Backend UserType enum values (query param `usertype` / `userType`). */
export const ONBOARDING_USER_TYPE_ENUM = {
  BROKER: 1,
  ORG_USER: 2,
  NORMAL_USER: 3,
};

/** Maps internal user-type strings to backend UserType enum integers. */
export function toOnboardingUserTypeEnum(userType) {
  if (userType === ONBOARDING_USER_TYPE.BROKER) return ONBOARDING_USER_TYPE_ENUM.BROKER;
  if (userType === ONBOARDING_USER_TYPE.ORG_USER) return ONBOARDING_USER_TYPE_ENUM.ORG_USER;
  if (userType === ONBOARDING_USER_TYPE.NORMAL_USER) return ONBOARDING_USER_TYPE_ENUM.NORMAL_USER;

  const numeric = Number(userType);
  if (
    numeric === ONBOARDING_USER_TYPE_ENUM.BROKER ||
    numeric === ONBOARDING_USER_TYPE_ENUM.ORG_USER ||
    numeric === ONBOARDING_USER_TYPE_ENUM.NORMAL_USER
  ) {
    return numeric;
  }

  return null;
}

function trim(value) {
  return value == null ? '' : String(value).trim();
}

function getSearchParam(searchParams, ...keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const value = trim(searchParams.get(keys[i]));
    if (value) return value;
  }
  return '';
}

function normalizeUserType(raw) {
  const value = trim(raw);
  if (!value) return null;

  const numeric = Number(value);
  if (!Number.isNaN(numeric)) {
    if (numeric === ONBOARDING_USER_TYPE_ENUM.BROKER) return ONBOARDING_USER_TYPE.BROKER;
    if (numeric === ONBOARDING_USER_TYPE_ENUM.ORG_USER) return ONBOARDING_USER_TYPE.ORG_USER;
    if (numeric === ONBOARDING_USER_TYPE_ENUM.NORMAL_USER) return ONBOARDING_USER_TYPE.NORMAL_USER;
  }

  const normalized = value.toLowerCase().replace(/[\s_-]+/g, '');
  if (normalized === 'broker' || normalized === 'brokers') {
    return ONBOARDING_USER_TYPE.BROKER;
  }
  if (normalized === 'orguser' || normalized === 'orgusers') {
    return ONBOARDING_USER_TYPE.ORG_USER;
  }
  if (
    normalized === 'normaluser' ||
    normalized === 'normal' ||
    normalized === 'user' ||
    normalized === 'enduser'
  ) {
    return ONBOARDING_USER_TYPE.NORMAL_USER;
  }
  return null;
}

/**
 * Resolves the invitation / staged user id from link params or registration state.
 * Accepts legacy `id` / `invitationId` query aliases as `userId`.
 */
export function resolveOnboardingUserId(linkParams = {}, registration = {}) {
  return trim(
    linkParams.userId ||
      registration.invitation?.userId ||
      registration.invitation?.invitationId ||
      linkParams.invitationId,
  );
}

/**
 * Parses onboarding invitation / promo query string.
 *
 * Canonical invitation link:
 *   /onboarding/registration?userId=<uuid>&usertype=1|2|3&email=<email>
 *
 * Promo / subscription-key link:
 *   /onboarding/registration?promoCode=<code>&usertype=2|3
 *   /onboarding/registration?subscriptionKey=<key>&usertype=2|3
 *
 * Legacy aliases still accepted: `id` / `invitationId` → userId, `userType` / `type`.
 * Invitation `token` is no longer used for onboarding flow decisions.
 * Promo / key is never treated as broker onboarding.
 *
 * @param {string} search
 */
export function parseOnboardingLinkSearch(search) {
  const searchParams = new URLSearchParams(search || '');
  const email = getSearchParam(searchParams, 'email', 'Email', 'userEmail');
  const userType = normalizeUserType(
    getSearchParam(searchParams, 'userType', 'usertype', 'UserType', 'type'),
  );
  const subscriptionKey = getSearchParam(
    searchParams,
    'subscriptionKey',
    'SubscriptionKey',
    'key',
  );
  const promoCode = getSearchParam(searchParams, 'promoCode', 'PromoCode', 'promo');
  // Canonical userId; `id` / `invitationId` remain aliases for existing invite emails.
  const userId = getSearchParam(searchParams, 'userId', 'id', 'invitationId');
  const brokerageId = getSearchParam(searchParams, 'brokerageId');

  const isBrokerPath = userType === ONBOARDING_USER_TYPE.BROKER;
  const isOrgUserPath = userType === ONBOARDING_USER_TYPE.ORG_USER;
  const isNormalUserPath = userType === ONBOARDING_USER_TYPE.NORMAL_USER;

  const hasInvitationUser = Boolean(userId);
  const hasPromoOrKey = Boolean(promoCode || subscriptionKey);
  const hasActivationFromLink = hasInvitationUser || hasPromoOrKey;
  // Invitation links can open without email — validate-invitation prefills it.
  const canAutoStart = Boolean(
    userType && hasActivationFromLink && (email || hasInvitationUser || hasPromoOrKey),
  );

  return {
    email,
    userType,
    subscriptionKey,
    promoCode,
    userId,
    // Kept for older call sites that still read invitationId.
    invitationId: userId,
    brokerageId,
    hasInvitationUser,
    hasActivationFromLink,
    isBrokerPath,
    isOrgUserPath,
    isNormalUserPath,
    canAutoStart,
    emailFromLink: Boolean(email),
  };
}

export function getOnboardingPathLabel(userType) {
  if (userType === ONBOARDING_USER_TYPE.BROKER) return 'Broker';
  if (userType === ONBOARDING_USER_TYPE.ORG_USER) return 'Organisation user';
  if (userType === ONBOARDING_USER_TYPE.NORMAL_USER) return 'Organisation user';
  return null;
}
